// "정책 레지스트리": 특정 리소스(resource)와 액션(action)에 필요한 최소 역할(minRole)을 선언.
// - isAllowedByPolicy(role, resource, action) 로 판정
// - ensureAllowed(...) 는 미승인 시 에러를 던져 인터셉터/가드가 처리하도록 함
// - mergePolicies(extra) 로 동적 확장(페이지/API 추가) 가능

import { ROLES, ROLE_RANK } from '../roles';
import {
  ACTIONS,
  RESOURCES,
  isPageResource,
  isApiResource,
} from '../resources';

// ------------------------------
// 정책 테이블(기본값)
// ------------------------------
/**
 * policy item shape:
 * {
 *   resource: string,         // e.g. 'page:admin_dashboard', 'api:grns'
 *   action: string,           // ACTIONS.VIEW | READ | CREATE | UPDATE | DELETE | APPROVE ...
 *   minRole: ROLES.<...>,     // 최소 요구 역할
 * }
 */
const BASE_POLICIES = [
  // ---------- 페이지(메뉴/화면) ----------
  // Admin 섹션: manager 이상 접근 가능 (superuser, tenant_admin, manager)
  {
    resource: RESOURCES.PAGE_ADMIN_DASHBOARD,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.PAGE_ADMIN_USERS,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.PAGE_ADMIN_WAREHOUSES,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.PAGE_ADMIN_VENDORS,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.PAGE_ADMIN_SEQUENCES,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.PAGE_ADMIN_SHIPPING_PROVIDERS,
    action: ACTIONS.VIEW,
    minRole: ROLES.MANAGER,
  },

  // 일반 WMS 화면: staff 이상 접근
  {
    resource: RESOURCES.PAGE_PRODUCTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_PURCHASE_ORDERS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  { resource: RESOURCES.PAGE_GRNS, action: ACTIONS.VIEW, minRole: ROLES.STAFF },
  {
    resource: RESOURCES.PAGE_PUT_AWAY,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_PICKLISTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_SHIPMENTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_MANIFESTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_RETURN_MANIFESTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_SEARCH,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_SHELVES,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_ORDERS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_VENDORS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_WAREHOUSE_STOCK,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_CHANNELS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.PAGE_CHANNEL_PRODUCTS,
    action: ACTIONS.VIEW,
    minRole: ROLES.STAFF,
  },

  // ---------- API(도메인 단위) ----------
  // 특수 API(컨텍스트 미주입)
  { resource: RESOURCES.API_AUTH, action: ACTIONS.READ, minRole: ROLES.GUEST }, // login status 등
  {
    resource: RESOURCES.API_AUTH,
    action: ACTIONS.CREATE,
    minRole: ROLES.GUEST,
  }, // login
  { resource: RESOURCES.API_ME, action: ACTIONS.READ, minRole: ROLES.STAFF }, // 내 권한 읽기

  // WMS 도메인 (일반 규칙 예시)
  {
    resource: RESOURCES.API_WAREHOUSES,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_WAREHOUSES,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },

  {
    resource: RESOURCES.API_PRODUCTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PRODUCTS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_PRODUCTS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_PRODUCTS,
    action: ACTIONS.DELETE,
    minRole: ROLES.MANAGER,
  },

  {
    resource: RESOURCES.API_PURCHASE_ORDERS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PURCHASE_ORDERS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_PURCHASE_ORDERS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },

  { resource: RESOURCES.API_GRNS, action: ACTIONS.READ, minRole: ROLES.STAFF },
  {
    resource: RESOURCES.API_GRNS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_GRNS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  { resource: RESOURCES.API_GRNS, action: ACTIONS.PRINT, minRole: ROLES.STAFF },

  {
    resource: RESOURCES.API_PUT_AWAY,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PUT_AWAY,
    action: ACTIONS.CREATE,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PUT_AWAY,
    action: ACTIONS.UPDATE,
    minRole: ROLES.STAFF,
  },

  {
    resource: RESOURCES.API_PICKLISTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PICKLISTS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_PICKLISTS_STATUS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PICKLISTS_PICKED,
    action: ACTIONS.UPDATE,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_PICKLISTS_SHIPMENT,
    action: ACTIONS.CREATE,
    minRole: ROLES.STAFF,
  },

  {
    resource: RESOURCES.API_INVENTORY,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_SHIPMENTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_MANIFESTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_RETURN_MANIFESTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },

  {
    resource: RESOURCES.API_VENDORS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_VENDORS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_VENDORS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },

  {
    resource: RESOURCES.API_SHELVES,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_SHELVES,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_SHELVES,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },

  {
    resource: RESOURCES.API_CHANNELS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_CHANNEL_PRODUCTS,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },

  {
    resource: RESOURCES.API_BULK_ACTIONS,
    action: ACTIONS.CREATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_EXPORTS,
    action: ACTIONS.EXPORT,
    minRole: ROLES.STAFF,
  },
  {
    resource: RESOURCES.API_SEARCH,
    action: ACTIONS.READ,
    minRole: ROLES.STAFF,
  },

  // Management/* (관리자 전용: manager 이상)
  {
    resource: RESOURCES.API_MGMT_USERS,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_USERS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_WAREHOUSES,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_WAREHOUSES,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_VENDORS,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_VENDORS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SHELVES,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SHELVES,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SEQUENCES,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SEQUENCES,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SHIPPING_PROVIDERS,
    action: ACTIONS.READ,
    minRole: ROLES.MANAGER,
  },
  {
    resource: RESOURCES.API_MGMT_SHIPPING_PROVIDERS,
    action: ACTIONS.UPDATE,
    minRole: ROLES.MANAGER,
  },
];

// ------------------------------
// 내부 저장(확장 가능)
// ------------------------------
let POLICY_REGISTRY = [...BASE_POLICIES];

// 외부에서 정책 추가(머지)
export function mergePolicies(extra = []) {
  if (!Array.isArray(extra)) return;
  POLICY_REGISTRY = [...POLICY_REGISTRY, ...extra];
}

// 조회
export function getPolicy(resource, action) {
  return (
    POLICY_REGISTRY.find(p => p.resource === resource && p.action === action) ||
    null
  );
}

// 판정
export function isAllowedByPolicy(role, resource, action) {
  const policy = getPolicy(resource, action);
  if (!policy) {
    // 보안 기본값: 등록 안 된 리소스/액션은 차단
    return false;
  }
  const minRank = ROLE_RANK[policy.minRole] ?? Infinity;
  const myRank = ROLE_RANK[role] ?? -1;
  return myRank >= minRank;
}

// 실패 시 던질 에러 객체 생성(인터셉터/가드에서 식별)
export function buildPermissionError({ resource, action, role }) {
  const err = new Error('Permission denied');
  err.code = 'PERMISSION_ERROR';
  err.meta = { resource, action, role };
  return err;
}

// 보장(허용되지 않으면 throw)
export function ensureAllowed({ role, resource, action }) {
  if (!isAllowedByPolicy(role, resource, action)) {
    throw buildPermissionError({ resource, action, role });
  }
}

// ------------------------------
// (선택) HTTP ↔ ACTIONS 간 단순 매핑 유틸
//  - 인터셉터에서 메서드를 ACTIONS로 변환할 때 사용
// ------------------------------
export function httpMethodToAction(method) {
  switch (String(method || '').toUpperCase()) {
    case 'GET':
      return ACTIONS.READ;
    case 'POST':
      return ACTIONS.CREATE;
    case 'PUT':
    case 'PATCH':
      return ACTIONS.UPDATE;
    case 'DELETE':
      return ACTIONS.DELETE;
    default:
      return ACTIONS.READ;
  }
}

// 디버깅/개발용(정책 테이블 노출)
export function listPolicies() {
  return [...POLICY_REGISTRY];
}
